"use client";
import "../styles/globals.css";

import dynamic from "next/dynamic";
import { Italiana } from "next/font/google";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import React, { useEffect, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { withKeepAlive } from "react-next-keep-alive";

import { initializeApollo } from "@/apollo-client";
import HomeCarousel from "@/components/ui/carousel/home-carousel";
import Header from "@/components/ui/header";
import HomeCards from "@/components/ui/home-cards";
import SearchBar from "@/components/ui/search-bar";
import HOMEPAGE from "@/graphql/queries/homepage";
import SURPRISE from "@/graphql/queries/surprise";
import { MerchandisingRow, Search_Type } from "@/graphql/types/graphql";
import useTracker from "@/hooks/useTracker";
import CodeSandboxIcon from "@/public/codesandbox.svg";
import CPUIcon from "@/public/cpu.svg";
import LightBulbIcon from "@/public/light-bulb.svg";
import OptionsIcon from "@/public/options.svg";
import ZAPIcon from "@/public/zap.svg";
import { searchResultRoute } from "@/utils/search";
import { createStructuredData } from "@/utils/structuredData";

const apolloClient = initializeApollo();

const italiana = Italiana({
  subsets: ["latin"],
  weight: "400",
  display: "swap",
  preload: true, // important for LCP
});

const HowText1 =
  "Say goodbye to endless scrolling across countless sites. Plush helps you discover styles from brands and retailers worldwide, all finely categorized by AI.";
const HowText2 =
  "Find exactly what you envision, effortlessly. Our platform speaks your language and grasps your nuanced intents using sophisticated AI.";
const HowText3 =
  "Experience a platform that just gets you. Our AI quickly learns your style, and as your tastes evolve, so does our understanding of what you love.";

const metadata = {
  title: "Plush | AI-Powered Fashion Search and Discovery Platform",
  description:
    "Plush is a curated, personalized AI fashion search and discovery platform, bringing style advice, visual inspiration and shoppable products in one place.",
  canonical: "https://www.plush.shop",
  ogTitle: "Plush, AI-Powered Fashion Search and Discovery Platform",
  image: "https://www.plush.shop/social-post.png",
  siteName: "Plush",
  ogType: "website",
};

interface HomePageProps {
  preResults: MerchandisingRow[];
}

const SearchModal = dynamic(
  () => import("@/components/ui/search-pattern/modal"),
  {
    ssr: false,
    loading: () => null,
  }
);

export function Home({ preResults: homepage }: HomePageProps) {
  const [search, setSearch] = useState("");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchbarRef = React.useRef<HTMLInputElement | null>(null);
  const router = useRouter();
  const { searchEvent, homePageViewEvent } = useTracker();
  const [isSurpriseLoading, setIsSurpriseLoading] = React.useState(false);
  const [homeCardsPage, setHomeCardsPage] = useState(1);
  const [showHomeCardsSkeleton, setShowHomeCardsSkeleton] = useState(false);
  const isCardsRotate = useRef(true);

  const handleNewSearch = () => {
    setSearch("");
    if (searchbarRef && searchbarRef.current) {
      searchbarRef.current.focus();
    }
  };

  const onSearchModalClose = () => {
    setIsSearchModalOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const route = async (searchValue: string) => {
    if (router) {
      searchEvent({
        query: searchValue,
        smart_filter_content: "",
        original_query: searchValue,
      });
      setIsLoading(true);
      await router.push(searchResultRoute(searchValue));
    }
  };

  const handleSearchSubmit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    // @ts-expect-error ignore
    event.target.blur();
    route(search);
  };

  const handleSurprise = async () => {
    setIsSurpriseLoading(true);
    const { data } = await apolloClient.query({
      query: SURPRISE,
      fetchPolicy: "network-only",
    });
    if (data && data.surprise) {
      router.push(searchResultRoute(data.surprise));
    }
  };

  function onPlaceholderShow(idx: number) {
    if (isCardsRotate.current) {
      setShowHomeCardsSkeleton(true);
      setTimeout(() => {
        setHomeCardsPage(idx);
        setShowHomeCardsSkeleton(false);
      }, 800);
    }
  }

  useEffect(() => {
    homePageViewEvent({});
  }, []);

  // Reset all loading states after route changed
  useEffect(() => {
    const completeHandler = () => {
      setIsLoading(false);
      setIsSurpriseLoading(false);
    };

    router.events.on("routeChangeComplete", completeHandler);

    return () => {
      router.events.off("routeChangeComplete", completeHandler);
    };
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(createStructuredData({})),
        }}
      />
      <NextSeo
        title={metadata.title}
        description={metadata.description}
        canonical={metadata.canonical}
        openGraph={{
          title: metadata.ogTitle,
          description: metadata.description,
          url: metadata.canonical,
          images: [{ url: metadata.image, alt: "Plush" }],
          siteName: metadata.siteName,
          type: metadata.ogType,
        }}
      />

      <Header position="relative" />
      <main
        className="flex flex-col items-center justify-center bg-white page"
        style={{
          backgroundImage: "url(/pink-cloud.svg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
        }}
      >
        <div className="section-container flex flex-col items-center text-center justify-center page-body">
          <h1 className={`title-logo-text homepage-section`}>
            The <span style={{ fontStyle: "italic" }}>smarter</span> way to shop
            fashion
          </h1>
          <div className="title-container">
            <p className={`title-info-text`}>
              AI meets taste—only on Plush. Just ask.
            </p>
          </div>
          <div className="home-search-zone" id="searchBar">
            <SearchBar
              className="home-search-bar"
              value={search}
              position="relative"
              onChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              hasRotation
              isLoading={isLoading}
              ref={searchbarRef}
              onPlaceholderShow={onPlaceholderShow}
              onFocus={() => {
                console.log("Focus - setting rotation to false");
                isCardsRotate.current = false;
              }}
              onBlur={() => {
                isCardsRotate.current = true;
              }}
            />
          </div>
          <div className="homepage-section">
            <HomeCards
              page={homeCardsPage}
              showSkeleton={showHomeCardsSkeleton}
            />
          </div>
          <div className="section-container flex justify-center">
            <div className="guide-me" onClick={() => handleSurprise()}>
              {!isSurpriseLoading ? (
                <>
                  <LightBulbIcon />
                  <span>Surprise me</span>
                </>
              ) : (
                <Spinner animation="border" role="status" size="sm">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              )}
            </div>
            <div
              className="guide-me"
              onClick={() => setIsSearchModalOpen(true)}
            >
              <OptionsIcon />
              <span>Smart filters</span>
            </div>
          </div>

          <div className="section-container homepage-section carousel-container">
            {homepage &&
              homepage.map((row) => {
                return (
                  <div className="carousel-section" key={row.title}>
                    <HomeCarousel
                      {...row}
                      numberPerSlide={row.type === "Brand" ? 5 : 4}
                      searchType={Search_Type.Item}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </main>

      <div
        className="plush-works"
        style={{
          backgroundColor: "#F1F1F1",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div className="section-container flex flex-col items-center justify-center plush-works-content">
          <div
            className={`${italiana.className} how-works-text`}
            style={{ display: "flex", color: "#191919" }}
          >
            How Plush works
          </div>
          <div className="flex w-full justify-between items-start text-dark-gray plush_works-img-over">
            <div className="flex flex-col items-center howText-item">
              <CodeSandboxIcon className="how-logo" />
              <p className={`text-center break-words how-works-text-p`}>
                {HowText1}
              </p>
            </div>
            <div className="flex flex-col items-center howText-item">
              <CPUIcon className="how-logo" />
              <p className={`text-center break-words how-works-text-p`}>
                {HowText2}
              </p>
            </div>
            <div className="flex flex-col items-center howText-item">
              <ZAPIcon className="how-logo" />
              <p className={`text-center break-words how-works-text-p`}>
                {HowText3}
              </p>
            </div>
          </div>
        </div>
      </div>

      {isSearchModalOpen && (
        <SearchModal
          onHide={onSearchModalClose}
          initSearch={search}
          onNewSearch={handleNewSearch}
        />
      )}

      <style jsx>{`
        .homepage-section {
          padding: 0 72px;
        }
        .carousel-container {
          margin-top: 40px;
          .carousel-section {
            margin-bottom: 93px;
            &:last-child {
              margin-bottom: 100px;
            }
          }
        }
        .title-info-text {
          color: #666666;
          font-size: 20px;
          font-weight: 500;
          line-height: 24px;
        }
        .title-logo-text {
          color: #191919;
          line-height: normal;
          font-family: Times Now;
          font-size: 40px;
          font-weight: 300;
        }
        .home-search-zone {
          width: 800px;
          margin-top: 32px;
        }
        .plush_works-img-over {
          margin-top: 60px;
          background-size: containe;
          flex-direction: row;
        }
        .how-works-text {
          font-size: 40px;
          weight: 400;
        }
        .how-works-text-p {
          font-size: 16px;
          font-weight: 500;
          margin-top: 45px;
        }
        .how-logo {
          width: 70px;
          height: 70px;
        }
        .howText-item {
          padding: 0 40px;
          max-width: 395px;
        }

        .plush-works-content {
          padding: 72px;
        }

        .page-body {
          margin-top: 64px;
        }

        .title-container {
          display: flex;
          align-items: center;
          max-width: 520px;
          padding: 16px;
          width: 100%;
          justify-content: center;
        }

        .guide-me {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          border: 1px solid #e7e7e7;
          cursor: pointer;
          margin: 40px 6px 0 6px;

          color: #191919;
          font-size: 14px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: -0.28px;

          > span {
            margin-left: 8px;
          }
        }
        @media (max-width: 768px) {
          .guide-me {
            margin-top: 24px;
          }
          .homepage-section {
            padding: 0 24px;
          }
          .title-container {
            padding: 0;
            max-width: 313px;
          }
          .carousel-container {
            margin-top: 48px;
            .carousel-section {
              margin-bottom: 70px;
              &:last-child {
                margin-bottom: 72px;
              }
            }
          }
          .home-search-zone {
            width: 85vw;
            margin-top: 24px;
          }
          .title-logo-text {
            line-height: 1;
            font-size: 32px;
            -webkit-font-smoothing: antialiased;
          }
          .title-info-text {
            font-weight: 500;
            margin-top: 16px;
            font-size: 16px;
            -webkit-font-smoothing: antialiased;
          }
          .title-icon {
            display: none;
          }
          .header-zone-margin {
            margin-top: -38px;
          }
          .plush-works {
            height: auto;
          }
          .how-works-text {
            font-size: 32px;
            font-weight: 400;
          }
          .how-works-text-p {
            font-size: 14px;
            font-weight: 500;
            margin-top: 28px;
            margin-bottom: 0;
          }
          .howText-item {
            margin-left: 0;
            height: auto;
            margin-top: 51px;
          }
          .how-logo {
            width: 40px;
            height: 40px;
          }
          .plush-works-content {
            padding: 40px 24px 57px;
          }
          .plush_works-img-over {
            align-items: center;
            margin-top: 0;
            flex-direction: column;
          }
          .page-body {
            margin-top: 40px;
          }
        }
      `}</style>
      <style global jsx>{`
        .home-search-bar {
          width: 800px;
        }
        .how-logo {
          @media (max-width: 768px) {
            width: 40px;
            height: 40px;
          }
        }
      `}</style>
    </>
  );
}

// @ts-expect-error ignore props type
export default withKeepAlive(Home, ({ router }) => router.asPath, {
  // Disabled this to avoid conflict with the nextjs scroll restoration
  keepScrollEnabled: false,
});

export async function getStaticProps() {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query({
    query: HOMEPAGE,
  });

  return {
    props: { preResults: data?.homepage || [] },
  };
}
